/**
 * Is a decoded avatar bitmap an actual photo, or a placeholder standing in for one?
 *
 * A stored photo can be a 1x1 PNG — anonymised rows carry exactly that in `profile.photoUrl`, and
 * the avatar endpoint serves it as a perfectly valid image. The browser therefore reports the
 * image as LOADED, Radix's <Avatar> suppresses <AvatarFallback>, and the circle renders as one
 * stretched pixel: a blank disc where the person's initials should be. Measuring the decoded
 * bitmap is the only way to tell the two apart — the URL, the status code and the MIME type are
 * identical for both.
 */

/**
 * Smallest edge (px) a decoded avatar must have to count as a real photo.
 *
 * Nothing a camera or a WhatsApp sync produces is anywhere near this small (the server caps
 * avatars at 512px and never upscales), so the threshold only ever catches placeholders while
 * staying well clear of a genuinely low-res thumbnail.
 */
export const MIN_USABLE_AVATAR_PX = 8;

/**
 * @param {number} width Decoded natural width, px.
 * @param {number} height Decoded natural height, px.
 * @returns {boolean} true when the bitmap is big enough to show instead of the initials.
 */
export function isUsableAvatarSize(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h)) return false;
  return w >= MIN_USABLE_AVATAR_PX && h >= MIN_USABLE_AVATAR_PX;
}
